import * as React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Paper,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PhoneIcon from "@mui/icons-material/Phone";
import ContactsIcon from "@mui/icons-material/Contacts";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import InsightsIcon from "@mui/icons-material/Insights";
import PageTitle from "../components/PageTitle";

export default function Home() {
  const theme = useTheme();
  const navigate = useNavigate();

  const quickAccessCards = [
    {
      title: "Recorded Calls",
      description: "Access and manage your recorded call history",
      icon: <PhoneIcon sx={{ fontSize: 40 }} />,
      path: "/recorded-calls",
      gradient: "linear-gradient(135deg, #6B8DE3 0%, #3B5998 100%)",
      stats: { value: "2,547", label: "Total Calls" }
    },
    {
      title: "Contact Management",
      description: "View and manage recorded and third-party contacts",
      icon: <ContactsIcon sx={{ fontSize: 40 }} />,
      path: "/recorded-contacts",
      gradient: "linear-gradient(135deg, #FF6B6B 0%, #C62E2E 100%)",
      stats: { value: "1,284", label: "Active Contacts" }
    },
    {
      title: "Playlists",
      description: "Organize and access your call playlists",
      icon: <HeadphonesIcon sx={{ fontSize: 40 }} />,
      path: "/playlists",
      gradient: "linear-gradient(135deg, #50C878 0%, #2E8B57 100%)",
      stats: { value: "156", label: "Playlists" }
    },
    {
      title: "Insights",
      description: "Analyze contact performance and metrics",
      icon: <InsightsIcon sx={{ fontSize: 40 }} />,
      path: "/insights/recorded-contacts",
      gradient: "linear-gradient(135deg, #9B6BFF 0%, #6E3CBC 100%)",
      stats: { value: "98%", label: "Compliance Rate" }
    },
  ];

  const complianceMetrics = [
    {
      title: "Call Quality Score",
      value: 92,
      color: "#4CAF50"
    },
    {
      title: "Compliance Adherence",
      value: 98,
      color: "#2196F3"
    },
    {
      title: "Risk Assessment",
      value: 85,
      color: "#FF9800"
    }
  ];

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "hidden", backgroundColor: theme.palette.background.paper }}>
    <Box sx={{ p: 3, maxWidth: "1600px", margin: "0 auto", } }>
      <Box
        sx={{
          background: "linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.1) 100%)",
          borderRadius: 4,
          p: 4,
          mb: 4,
        }}
      >
        <PageTitle 
          title="Welcome to Compliance UI Portal"
        />
        
        <Typography 
          variant="body1" 
          sx={{ 
            mt: 1,
            color: theme.palette.text.secondary,
            maxWidth: 800,
            fontSize: "1.1rem",
            lineHeight: 1.6
          }}
        >
          Manage and analyze your recorded calls, contacts, and compliance data all in one place. Access key features and insights quickly through the dashboard below.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {quickAccessCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: card.gradient,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.2)',
                  '& .card-shine': {
                    transform: 'translateX(100%)',
                  }
                }
              }}
              onClick={() => navigate(card.path)}
            >
              <Box
                className="card-shine"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transition: 'transform 0.5s ease',
                }}
              />
              <CardContent sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 3
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {card.icon}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {card.stats.value}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {card.stats.label}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {card.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: theme.palette.text.primary }}>
          Compliance Overview
        </Typography>
        <Grid container spacing={3}>
          {complianceMetrics.map((metric, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper 
                sx={{ 
                  p: 3,
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                  {metric.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mr: 1 }}>
                    {metric.value}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={metric.value} 
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: `${metric.color}20`,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: metric.color,
                      borderRadius: 4,
                    }
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
      </Box>
    </Box>
  );
}
